/**
 * lib/nako.ts
 * ------------------------------------------------------------------
 * Opening Nako, the timetable editor, already signed in (2 Nov 2026).
 *
 * Nako is its own site (lkc-nako.web.app) on the same Firebase project, so it
 * accepts portal accounts, but a sign-in does not cross sites. The portal
 * opens Nako in its own tab, asks functions/nako.js for a custom token for
 * the signed-in timetabler, and posts it to that tab when Nako says it is
 * ready — addressed to Nako's origin only, never in a URL. Nako checks the
 * message came from the portal window that opened it, then signs in.
 *
 * The tab has to be opened inside the click (a browser blocks a tab opened
 * after an await), so the token is fetched while Nako loads.
 * ------------------------------------------------------------------
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';

export const NAKO_URL = 'https://lkc-nako.web.app';
const NAKO_ORIGIN = new URL(NAKO_URL).origin;

/** Opens Nako and signs it in. Call from a click handler. Resolves once the
 *  token has been handed over; rejects with a sentence for the user. */
export function openNako(): Promise<void> {
  const tab = window.open(`${NAKO_URL}/?from=portal`, 'lkc-nako');
  if (!tab) return Promise.reject(new Error('The browser blocked the new tab. Allow pop-ups for the portal and try again.'));

  const token = httpsCallable<void, { token: string }>(functions, 'nakoSignIn')().then((r) => r.data.token);

  return new Promise((resolve, reject) => {
    const stop = () => { window.removeEventListener('message', onMessage); clearTimeout(timer); };
    const fail = (e: unknown) => { stop(); reject(new Error((e as Error)?.message || 'Could not sign you in to Nako.')); };
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== NAKO_ORIGIN || e.source !== tab || (e.data as { type?: string })?.type !== 'nako-ready') return;
      token.then((t) => {
        tab.postMessage({ type: 'nako-token', token: t }, NAKO_ORIGIN);
        stop();
        resolve();
      }, fail);
    };
    window.addEventListener('message', onMessage);
    token.catch(fail);
    const timer = setTimeout(
      () => fail(new Error('Nako opened but did not answer. Sign in there with your portal password.')),
      30_000,
    );
  });
}
